import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { userSchema, UserFormData } from "@/react/schema/userSchema";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Label } from "@/modern/components/ui/label";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { getRolesWithoutPublic, getUserGrantsTextOnly } from "@/other/utils/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { useUserEditorContext } from "../context/UserEditorContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const ROLE_PERMISSION_FALLBACK = [{ description: "Please select a role to see permissions" }];

export const GeneralTab = () => {
  const { project, user: currentUser } = useSwivuBryntumContext();
  const { userRecord } = useUserEditorContext();
  const [permissionContent, setPermissionContent] = useState(ROLE_PERMISSION_FALLBACK);
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty }
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: userRecord.name || "",
      email: userRecord.email || "",
      roles: userRecord.roles
    }
  });

  const userRole = watch("roles");
  const appRoles = (project as any)?.swivuAppRoles;

  const roles = useMemo((): { label: string; value: number; disabled: boolean }[] => {
    return getRolesWithoutPublic(appRoles ?? []).map((role: { id: number; name: string; grants?: { value: number }[] }) => {
      const minGrant = role.grants?.length
        ? Math.min(...role.grants.map((grant) => grant.value))
        : undefined;
      return {
        label: role.name,
        value: role.id,
        disabled: minGrant != null && currentUser ? !currentUser.can(minGrant) : false,
      };
    });
  }, [appRoles, currentUser]);

  const handleRoleChange = (value: string) => {
    setValue("roles", parseInt(value), { shouldDirty: true });
  };

  const handleSetPermissionContent = (roleId: number) => {
    const role = (appRoles ?? []).find((record: { id: number }) => record.id === roleId);
    if (!role?.grants?.length) {
      setPermissionContent(role ? [{ description: "No permissions" }] : ROLE_PERMISSION_FALLBACK);
      return;
    }
    const grantsText = getUserGrantsTextOnly(role.grants);
    setPermissionContent(grantsText?.length ? grantsText : ROLE_PERMISSION_FALLBACK);
  };

  const onSaveUser = async (data: UserFormData) => {
    if (!project) return;

    const boardUsersStore = project.boardUsersStore;

    // Check if email already exists
    const isUserEnteredEmailExists = boardUsersStore.find((record: any) => record.email && record.email.toLowerCase() === data.email.toLowerCase() && record.id !== userRecord?.id);

    if (isUserEnteredEmailExists) {
      toast.error("The email is already in use!");
      return;
    }

    // Update the user record
    if (userRecord) {
      setIsSaving(true);
      userRecord.name = data.name;
      userRecord.email = data.email;
      userRecord.roles = data.roles;

      if (userRecord.isPhantom) {
        boardUsersStore.add(userRecord);
      }
      await project.sync();
      setIsSaving(false);

      if (userRecord.isPhantom) {
        toast.success("A new user has been successfully added to this board.");
      } else {
        toast.success("User saved successfully");
      }
    }
  };

  const isCurrentUser = userRecord?.userId === currentUser?.id;
  const isPhantom = userRecord?.isPhantom ?? true;

  const descriptionText =
    "Swivu does NOT send invitations. Adding user contacts is optional and can be done later. <br/> All users simply login at www.swivu.com. Desktop users use Swivu HQ (this app). Mobile users use Swivu Workforce.";

  useEffect(() => {
    if (userRole) {
      handleSetPermissionContent(userRole);
    }
  }, [userRole]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-4">
        {isPhantom && (
          <div
            className="mb-3 leading-5 text-sm text-muted-foreground"
            dangerouslySetInnerHTML={{
              __html: `Enter a new board user; click save; and you are done! <br/> ${descriptionText}`
            }}
          />
        )}
        {!isPhantom && <div className="mb-3 leading-5 text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: descriptionText }} />}

        <form onSubmit={handleSubmit(onSaveUser)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">User Name *</Label>
            <Input id="name" {...register("name")} placeholder="Enter User Name" className="w-full" />
            {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              placeholder="Swivu users must have a Gmail or Google Workspace account. More options coming soon."
              className="w-full"
              disabled={isCurrentUser}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="roles">Role *</Label>
            <Select value={userRole !== undefined && userRole !== null ? String(userRole) : undefined} onValueChange={handleRoleChange} disabled={isCurrentUser}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Role" />
              </SelectTrigger>
              <SelectContent className="z-[--swivu-bryntum-z-index]">
                {roles.map((role) => (
                  <SelectItem key={role.value} value={String(role.value)} disabled={role.disabled}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.roles && <p className="text-sm text-destructive">{errors.roles.message}</p>}
          </div>
          <div className="space-y-2">
            <Label>Permissions</Label>
            <div className="space-y-1 text-sm text-muted-foreground">
              {permissionContent.map((content) => (
                <p key={content.description}>{content.description}</p>
              ))}
            </div>
          </div>
        </form>
      </div>
      <div className="flex justify-end p-4 pb-0 border-t border-border gap-2">
        <Button disabled={!isDirty || isSaving} onClick={handleSubmit(onSaveUser)}>
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <i className="fa-solid fa-floppy-disk" />}
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};
